// socket.ts — Singleton Socket.IO-client.
//
// Polku: /api/ws (sama origin → Caddy proxy backendiin).
// Auth: JWT-tokeni handshake.auth.token-kentässä, päivitetään refreshin yhteydessä.
//
// Käyttö: yleensä RealtimeProvider mountissa kutsuu connectSocket()/setSocketToken().
// Komponentit subscribtavat eventteihin useRealtimeEvent-hookilla.

import { io, type Socket } from 'socket.io-client';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001/api';

// Strippaa /api-prefix ja muotoile origin-urliksi: backend kuuntelee
// /api/ws path:ssa joten origin on BASE_URL ilman /api-suffixia.
function getSocketOrigin(): string {
  // Jos BASE_URL päättyy '/api', poistetaan se
  const stripped = BASE_URL.replace(/\/api\/?$/, '');
  return stripped || window.location.origin;
}

let socket: Socket | null = null;
let currentToken: string | null = null;

// Löydös F1: token-refresh (12 min) luo uuden Socket-olion. Aiemmin komponenttien
// useRealtimeEvent-listenerit oli kytketty vanhaan olioon eikä niitä liitetty uuteen
// → realtime "kuoli" ~12 min jälkeen (WorkPage ei päivittynyt ilman reloadia). Lisäksi
// jos socket oli null mount-hetkellä (login jälkeinen 1. sivu), listeneriä ei liitetty
// lainkaan. Ratkaisu: pysyvä listener-rekisteri, joka liitetään aina uuteen socketiin.
type Listener = (data: any) => void;
const listenerRegistry = new Map<string, Set<Listener>>();

function attachAllListeners(s: Socket): void {
  for (const [event, set] of listenerRegistry) {
    for (const l of set) s.on(event, l);
  }
}

/**
 * Tilaa realtime-eventti pysyvästi (säilyy socketin uudelleenluonnin yli).
 * Palauttaa unsubscribe-funktion. Käytä tätä useRealtimeEvent-hookin kautta.
 */
export function subscribe(event: string, listener: Listener): () => void {
  let set = listenerRegistry.get(event);
  if (!set) { set = new Set(); listenerRegistry.set(event, set); }
  set.add(listener);
  if (socket) socket.on(event, listener); // liitä heti jos socket jo olemassa
  return () => {
    set!.delete(listener);
    if (set!.size === 0) listenerRegistry.delete(event);
    if (socket) socket.off(event, listener);
  };
}

export function connectSocket(token: string): Socket {
  if (socket && socket.connected && currentToken === token) {
    return socket;
  }

  // Disconnect vanha jos token vaihtui
  if (socket) {
    socket.disconnect();
    socket = null;
  }

  currentToken = token;

  socket = io(getSocketOrigin(), {
    path: '/api/ws',
    auth: { token },
    transports: ['websocket', 'polling'], // pref websocket, fallback polling
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
    timeout: 10_000,
  });

  // F1: liitä kaikki rekisteröidyt listenerit uuteen socket-olioon
  attachAllListeners(socket);

  socket.on('connect', () => {
     
    console.debug('[realtime] connected:', socket?.id);
  });

  socket.on('connect_error', (err) => {
     
    console.warn('[realtime] connect_error:', err.message);
  });

  socket.on('auth:error', (data) => {
     
    console.warn('[realtime] auth error:', data);
    // Server pakottaa disconnectin — anna sen tapahtua
  });

  socket.on('disconnect', (reason) => {
     
    console.debug('[realtime] disconnected:', reason);
  });

  return socket;
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
    currentToken = null;
  }
}

export function getSocket(): Socket | null {
  return socket;
}

/**
 * Päivitä socketin token (esim. refreshin jälkeen). Jos socket on jo
 * yhdistetty samalla tokenilla, ei tehdä mitään. Muuten reconnect
 * uudella tokenilla.
 */
export function setSocketToken(token: string | null): void {
  if (token === currentToken) return;
  if (!token) {
    disconnectSocket();
    return;
  }
  connectSocket(token);
}
